/**
 * The dashboard.
 *
 * Four panels are real. The fifth says why it is not, rather than being filled
 * with something plausible -- there is no candidate-sourcing field anywhere in
 * the API, so a source breakdown would have to be invented.
 *
 * Every panel states the sample it was computed over. That is not decoration:
 * there is no aggregate endpoint, so these figures come from however many jobs
 * we managed to fan out over, and a number without its denominator invites the
 * reader to assume it covers everything.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Layout from '../../../../components/layout';
import Breadcrumb from '../../../../components/common/breadcrumb';
import CaptionInfo from '../../../../components/common/caption-info';
import { User } from '../../../../fsm/users';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useSetupChecklist } from '../../hooks/useSetupChecklist';
import SetupChecklist from '../../components/setup-checklist';
import WelcomeNote from '../../components/welcome-note';
import ByJobTable from '../../components/by-job-table';
import { useEngagementMetrics } from '../../hooks/useEngagementMetrics';
import {
  buildActivityFeed,
  distributeScores,
  measureTimeToHire,
  summarisePipeline,
  stagesFromFunnel,
  SOURCE_UNKNOWN,
} from '../../utils/aggregate';
import { statusTone, toneStyle } from '../../../jobs/utils/statusPalette';
import styles from './index.module.css';

const STAGE_LABELS: Record<string, string> = {
  NEW: 'New',
  REVIEWED: 'Reviewed',
  INVITATION_SENT: 'Invited',
  RIO_SCREENING: 'Rio screening',
  ALT_INTERVIEW: 'Alt interview',
  HM_REVIEW: 'HM review',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  NOT_INTERESTED: 'Not interested',
};

const label = (s: string) => STAGE_LABELS[s] ?? s;

const relative = (iso: string): string => {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

const breadcrumbItems = [{ title: 'Dashboard' }];

interface Props {
  user?: User;
}

const Dashboard = ({ user }: Props) => {
  const data = useDashboardData();
  const setupChecklist = useSetupChecklist(user);
  const metrics = useEngagementMetrics();
  const [searchParams, setSearchParams] = useSearchParams();
  // Set by the HM activation redirect on first login after accepting an
  // invite -- see ActivateAccount. Stays visible across a refresh (this is
  // ambient state, not a toast) until explicitly dismissed, which strips it
  // from the URL so it can't reappear on a later, unrelated visit.
  const welcomeCustomer = searchParams.get('welcome');
  const dismissWelcomeNote = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('welcome');
    setSearchParams(next, { replace: true });
  };

  const pipeline = useMemo(
    () => summarisePipeline(data.jobs, data.candidates),
    [data.jobs, data.candidates],
  );
  // Funnel stage counts are estate-wide and uncapped when they load; the
  // candidate fan-out's own stage counts (above) are the fallback while
  // metrics are loading or if that call fails, and remain the only source
  // for the terminal row -- the funnel endpoint excludes terminal stages.
  const funnelPipeline = useMemo(
    () =>
      metrics.funnelStages ? stagesFromFunnel(metrics.funnelStages) : null,
    [metrics.funnelStages],
  );
  const stageRows = funnelPipeline?.stages ?? pipeline.stages;
  const inPipelineCount = funnelPipeline?.inPipeline ?? pipeline.inPipeline;
  const scores = useMemo(
    () => distributeScores(data.jobs, data.candidates),
    [data.jobs, data.candidates],
  );
  const tth = useMemo(
    () => measureTimeToHire(data.jobs, data.hired),
    [data.jobs, data.hired],
  );
  const feed = useMemo(
    () => buildActivityFeed(data.jobs, data.candidates),
    [data.jobs, data.candidates],
  );

  const maxBucket = Math.max(1, ...scores.buckets.map((b) => b.count));
  const maxStage = Math.max(1, ...stageRows.map((s) => s.count));

  return (
    <Layout user={user}>
      <Breadcrumb
        items={breadcrumbItems}
        renderRight={
          <Button
            icon={<ReloadOutlined />}
            onClick={data.reload}
            disabled={data.loading}
          >
            Refresh
          </Button>
        }
      />

      <div className={styles.wrapper}>
        {welcomeCustomer && (
          <WelcomeNote
            customerName={welcomeCustomer}
            onDismiss={dismissWelcomeNote}
          />
        )}
        <div className={styles.header}>
          <h1 className={styles.title}>Dashboard</h1>
          {data.loading && <Spin size="small" />}
          {/* Coverage sits on the title's line. Stacked, the heading and this
              one sentence cost 248px before any figure appeared -- 28% of a
              900px viewport spent on chrome. */}
          {!data.error && data.jobsCovered > 0 && (
            <span className={styles.coverageInline}>
              {data.jobsCovered} of {data.totalJobs}{' '}
              {data.totalJobs === 1 ? 'job' : 'jobs'} ·{' '}
              {pipeline.sample.candidates}{' '}
              {pipeline.sample.candidates === 1 ? 'candidate' : 'candidates'}
            </span>
          )}
        </div>

        {data.error && <p className={styles.error}>{data.error}</p>}

        {/* What these figures cover is now on the heading's line. This block
            survives for the CAVEATS only -- what is missing from the numbers.
            Those are the part a reader can be misled by, so they keep their
            own line and are not folded into the header, and this renders
            nothing at all when there is nothing to caveat.

            A cap is a filter, not a problem, and "N jobs are not included"
            read like one -- reframed as what IS shown plus a way to see the
            rest, the same shape as a paginated list's own "showing X of Y". */}
        {!data.error && data.jobsSkipped > 0 && (
          <p className={styles.coverage}>
            Showing your {data.jobsCovered} most recent{' '}
            {data.jobsCovered === 1 ? 'job' : 'jobs'} ·{' '}
            <Link to="/jobs">View all {data.totalJobs}</Link>
          </p>
        )}
        {!data.error && data.jobsFailed > 0 && (
          <p className={styles.coverage}>
            <span className={styles.warn}>
              {data.jobsFailed} {data.jobsFailed === 1 ? 'job' : 'jobs'} could
              not be loaded, so figures below are incomplete.
            </span>
          </p>
        )}

        <SetupChecklist
          calendarConnected={setupChecklist.calendarConnected}
          teammateInvited={setupChecklist.teammateInvited}
        />

        <div className={styles.grid}>
          {/* --------------------------------------------- engagement */}
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Engagement</h2>
            {metrics.error ? (
              <p className={styles.empty}>{metrics.error}</p>
            ) : metrics.engagement ? (
              <>
                <p className={styles.bigFigure}>
                  {Math.round(metrics.engagement.responseRate)}
                  <span className={styles.bigFigureUnit}>% response rate</span>
                </p>
                <p className={styles.panelNote}>
                  {metrics.engagement.totalApplicants} applicants,{' '}
                  {metrics.engagement.totalMatched} matched to a job — across
                  the whole estate, not just the jobs below.
                </p>
              </>
            ) : (
              <Spin size="small" />
            )}
          </section>

          {/* ---------------------------------------------- pipeline */}
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Pipeline</h2>
            <div className={styles.figures}>
              <div>
                <dt className={styles.figureLabel}>Open jobs</dt>
                <dd className={styles.figureValue}>{pipeline.openJobs}</dd>
              </div>
              <div>
                <dt className={styles.figureLabel}>Closed</dt>
                <dd className={styles.figureValue}>{pipeline.closedJobs}</dd>
              </div>
              <div>
                <dt className={styles.figureLabel}>In pipeline</dt>
                <dd className={styles.figureValue}>{inPipelineCount}</dd>
              </div>
            </div>

            <ul className={styles.stages}>
              {stageRows.map((s) => (
                <li key={s.stage} className={styles.stageRow}>
                  <span className={styles.stageName}>{label(s.stage)}</span>
                  <span className={styles.barTrack}>
                    <span
                      className={styles.barFill}
                      style={{ width: `${(s.count / maxStage) * 100}%` }}
                      aria-hidden="true"
                    />
                  </span>
                  <span className={styles.stageCount}>{s.count}</span>
                </li>
              ))}
            </ul>

            <ul className={styles.terminalRow}>
              {pipeline.terminal.map((s) => (
                <li key={s.stage}>
                  <span
                    className={styles.pill}
                    style={toneStyle(statusTone(s.stage))}
                  >
                    {label(s.stage)} {s.count}
                  </span>
                </li>
              ))}
            </ul>

            {pipeline.unrecognised.length > 0 && (
              <p className={styles.warn}>
                {pipeline.unrecognised.length} candidate status
                {pipeline.unrecognised.length === 1 ? '' : 'es'} not shown
                above: {pipeline.unrecognised.join(', ')}. This page needs
                updating.
              </p>
            )}
          </section>

          {/* ---------------------------------------- fit distribution */}
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Fit score distribution</h2>
            {scores.tooFewToPlot ? (
              <p className={styles.empty}>
                {scores.scored === 0
                  ? 'No candidate here carries a fit score yet.'
                  : `Only ${scores.scored} scored ${
                      scores.scored === 1 ? 'candidate' : 'candidates'
                    } — too few to read anything from a distribution.`}
              </p>
            ) : (
              <>
                <div className={styles.histogram}>
                  {scores.buckets.map((b) => (
                    <div key={b.from} className={styles.histCol}>
                      <div className={styles.histBarWrap}>
                        <div
                          className={styles.histBar}
                          style={{ height: `${(b.count / maxBucket) * 100}%` }}
                          role="img"
                          aria-label={`${b.count} candidates scoring ${b.from} to ${b.to}`}
                        />
                      </div>
                      <span className={styles.histLabel}>
                        {b.from}–{b.to}
                      </span>
                      <span className={styles.histCount}>{b.count}</span>
                    </div>
                  ))}
                </div>
                <p className={styles.panelNote}>
                  Median {scores.median}. {scores.scored} scored
                  {scores.unscored > 0 && (
                    <>
                      , {scores.unscored} not scored — counted separately, never
                      as zero
                    </>
                  )}
                  .
                </p>
              </>
            )}
          </section>

          {/* ------------------------------------------- time to hire */}
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              Time to hire
              <CaptionInfo
                label="About time to hire"
                text="Not stage-by-stage — measured from when the job was created to the hire's most recent status change."
              />
            </h2>
            {tth.tooFewToReport ? (
              <p className={styles.empty}>
                {tth.days.length === 0
                  ? 'No measurable hires in this sample.'
                  : `Only ${tth.days.length} measurable ${
                      tth.days.length === 1 ? 'hire' : 'hires'
                    } — too few for a median.`}
              </p>
            ) : (
              <>
                <p className={styles.bigFigure}>
                  {tth.medianDays}
                  <span className={styles.bigFigureUnit}>days, median</span>
                </p>
                {/* The sample size stays visible -- a figure without its
                    denominator invites the reader to assume it covers
                    everything. Only the method note moved to the heading's
                    caption. */}
                <p className={styles.panelNote}>
                  Across {tth.days.length} hires.
                </p>
              </>
            )}
            {tth.unmeasurable > 0 && (
              <p className={styles.panelNote}>
                {tth.unmeasurable} {tth.unmeasurable === 1 ? 'hire' : 'hires'}{' '}
                could not be measured — a missing or inconsistent timestamp.
              </p>
            )}
            {data.hiredDetailSkipped > 0 && (
              <p className={styles.warn}>
                {data.hiredDetailSkipped} further hires were not fetched, so
                this median covers only part of them.
              </p>
            )}
          </section>

          {/* ---------------------------------------- recent activity */}
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              Recent activity
              <CaptionInfo
                label="About recent activity"
                text="Shows the current status only, not what it changed from."
              />
            </h2>
            {feed.length === 0 ? (
              <p className={styles.empty}>
                No recorded activity in this sample.
              </p>
            ) : (
              <ul className={styles.feed}>
                {feed.map((e) => (
                  <li
                    key={`${e.kind}-${e.label}-${e.atMs}`}
                    className={styles.feedRow}
                  >
                    <span className={styles.feedWhen}>{relative(e.at)}</span>
                    <span className={styles.feedWhat}>
                      <strong>{e.label}</strong>
                      {e.kind === 'candidate' ? (
                        <>
                          {' '}
                          is now {label(e.status).toLowerCase()}
                          {e.jobTitle ? ` on ${e.jobTitle}` : ''}
                        </>
                      ) : (
                        <> is now {e.status.toLowerCase()}</>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ---------------------------------------- by job */}
          <ByJobTable jobs={data.jobs} byJob={data.byJob} />

          {/* ---------------------------------------- source breakdown */}
        </div>

        {/* Kept, but as a line rather than a card. It was a full panel whose
            entire content was an explanation of its own absence -- the
            statement is worth keeping and the card was not. */}
        {SOURCE_UNKNOWN && (
          <p className={styles.pendingNote}>
            <strong>Source breakdown</strong>
            <CaptionInfo
              label="About source breakdown"
              text="Not available yet — no field records where a candidate came from."
            />
          </p>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
